import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { getUser, setStoredUser, clearAuth, clearGalleryCache, refreshSession } from "../lib/api";
import { devWarn } from "../lib/log";

const AuthCtx = createContext(null);
const REFRESH_EVERY_MS = 10 * 60 * 1000; // the access cookie lives 15 minutes

const readSessionIdFromHash = () => {
  const m = (window.location.hash || "").match(/session_id=([^&]+)/);
  return m ? decodeURIComponent(m[1]) : null;
};

const readAuthState = () => new URLSearchParams(window.location.search).get("auth_state");
const GOOGLE_STATE_KEY = "duckify_google_state";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => getUser());
  const [config, setConfig] = useState(null);
  const [checked, setChecked] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [exchanging, setExchanging] = useState(() => !!readSessionIdFromHash());

  const adopt = useCallback((u) => {
    if (u) setStoredUser(u);
    else clearAuth();
    setUser(u || null);
  }, []);

  // Gate state + CSRF cookie first, then the cookie session (refresh slides it forward and tells us who we are).
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/config");
        if (live) setConfig(data);
      } catch {
        if (live) setConfig({ gated: true, code_login: false });
      }
      await api.get("/auth/csrf").catch(() => {});
      if (readSessionIdFromHash()) return;
      try {
        const { data } = await refreshSession();
        if (live) adopt(data.user);
      } catch {
        if (live) adopt(null);
      } finally {
        if (live) setChecked(true);
      }
    })();
    const onSignedOut = () => adopt(null);
    window.addEventListener("duckify-signed-out", onSignedOut);
    return () => {
      live = false;
      window.removeEventListener("duckify-signed-out", onSignedOut);
    };
  }, [adopt]);

  useEffect(() => {
    if (!user) return undefined;
    const t = setInterval(() => {
      refreshSession()
        .then((r) => adopt(r.data.user))
        .catch((e) => e?.response?.status === 401 && adopt(null));
    }, REFRESH_EVERY_MS);
    return () => clearInterval(t);
  }, [user, adopt]);

  // Emergent-managed Google auth returns to us at {origin}/?auth_state=...#session_id=... — verify the CSRF state,
  // never exchange while already signed in, exchange once, then clear the hash + query.
  useEffect(() => {
    const sid = readSessionIdFromHash();
    if (!sid) return;
    const expected = sessionStorage.getItem(GOOGLE_STATE_KEY);
    const got = readAuthState();
    const cleanUrl = () => window.history.replaceState(null, "", window.location.pathname);
    const done = () => {
      cleanUrl();
      setExchanging(false);
      setChecked(true);
    };
    sessionStorage.removeItem(GOOGLE_STATE_KEY);
    if (getUser() || !expected || !got || expected !== got) return done(); // stale/forged return or already signed in
    api
      .post("/auth/google", { session_id: sid, public_base: window.location.origin })
      .then(({ data }) => adopt(data.user))
      .catch((e) => devWarn("Google auth exchange failed", e?.response?.status || e?.message))
      .finally(done);
  }, [adopt]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.user) adopt(data.user);
    return data;
  }, [adopt]);

  const verifyMfa = useCallback(async (body) => {
    const { data } = await api.post("/auth/2fa/verify", body);
    adopt(data.user);
    return data;
  }, [adopt]);

  const startMfaSetup = useCallback(() => api.post("/auth/2fa/setup").then((r) => r.data), []);

  const confirmMfa = useCallback(async (code) => {
    const { data } = await api.post("/auth/2fa/confirm", { code });
    if (data.user) adopt(data.user);
    return data;
  }, [adopt]);

  const newRecoveryCodes = useCallback((body) => api.post("/auth/2fa/recovery-codes", body).then((r) => r.data), []);
  const startMfaMove = useCallback((body) => api.post("/auth/2fa/move/start", body).then((r) => r.data), []);
  const confirmMfaMove = useCallback((code) => api.post("/auth/2fa/move/confirm", { code }).then((r) => r.data), []);

  const forgotPassword = useCallback((email) => api.post("/auth/forgot-password", { email, public_base: window.location.origin }), []);

  const resetPassword = useCallback((token, password) => api.post("/auth/reset-password", { token, password }).then((r) => r.data), []);

  const changePassword = useCallback(async (current_password, new_password) => {
    const { data } = await api.post("/auth/password", { current_password, new_password });
    adopt(data.user);
    return data;
  }, [adopt]);

  const requestCode = useCallback((email) => api.post("/auth/request-code", { email }), []);

  const verify = useCallback(async (email, code) => {
    const { data } = await api.post("/auth/verify", { email, code, public_base: window.location.origin });
    adopt(data.user);
    setAuthOpen(false);
    return data.user;
  }, [adopt]);

  const startGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const state = `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    sessionStorage.setItem(GOOGLE_STATE_KEY, state);
    const redirectUrl = `${window.location.origin}/?auth_state=${state}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  // Ends the server session, then forgets it here even if that failed; `then` (e.g. navigate) renders with the sign-out.
  const signOut = useCallback(async (then) => {
    try {
      await api.post("/auth/logout", null, { timeout: 8000 });
    } catch (e) {
      devWarn("Sign-out request failed; signed out on this device anyway", e);
    }
    clearGalleryCache();
    sessionStorage.removeItem(GOOGLE_STATE_KEY);
    setAuthOpen(false);
    if (typeof then === "function") then();
    adopt(null);
  }, [adopt]);

  const sessionReady = checked && !!config;
  const value = useMemo(
    () => ({
      user, config, gated: config ? !!config.gated : null, sessionReady, isAuthed: !!user, exchanging, authOpen,
      openAuth: () => setAuthOpen(true), closeAuth: () => setAuthOpen(false),
      login, verifyMfa, startMfaSetup, confirmMfa, newRecoveryCodes, startMfaMove, confirmMfaMove, forgotPassword,
      resetPassword, changePassword, requestCode, verify, startGoogle, signOut,
    }),
    [user, config, sessionReady, exchanging, authOpen, login, verifyMfa, startMfaSetup, confirmMfa, newRecoveryCodes,
      startMfaMove, confirmMfaMove, forgotPassword, resetPassword, changePassword, requestCode, verify, startGoogle, signOut],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
